import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ChartsCriteria, SortDirection } from '@e-origin/shared';

import { apiClient } from '@utils';

export const defaultChartsCriteria: ChartsCriteria = {
  filters: { searchQuery: '' },
  pagination: { page: 1, size: 10 },
  sorting: { field: 'order', direction: SortDirection.ASC },
};

const parseUrlToCriteria = (searchParams: URLSearchParams): ChartsCriteria => {
  const filters: ChartsCriteria['filters'] = {};
  filters.searchQuery = searchParams.get('searchQuery') || '';
  filters.filterByDeclarants = searchParams.get('declarants')?.split(',')?.filter(Boolean) || [];

  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '10', 10);

  const sortField = searchParams.get('sortField') || 'order';
  const sortDir = (searchParams.get('sortDir') || SortDirection.ASC) as SortDirection;

  return {
    filters,
    pagination: { page, size },
    sorting: { field: sortField, direction: sortDir },
  };
};

const criteriaToSearchParams = (criteria: ChartsCriteria): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (criteria.filters.searchQuery) {
    searchParams.set('searchQuery', criteria.filters.searchQuery);
  }

  if (criteria.filters.filterByDeclarants) {
    searchParams.set('declarants', criteria.filters.filterByDeclarants.join(','));
  }

  searchParams.set('page', criteria.pagination.page.toString());
  searchParams.set('size', criteria.pagination.size.toString());

  searchParams.set('sortField', criteria.sorting.field);
  searchParams.set('sortDir', criteria.sorting.direction);

  return searchParams;
};

export const getInitialChartCriteria = (searchParams: URLSearchParams): ChartsCriteria => {
  if (searchParams.toString()) {
    return parseUrlToCriteria(searchParams);
  }
  return defaultChartsCriteria;
};

export const useChartCriteriaUrlSync = (criteria?: ChartsCriteria) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!criteria) return;

    const currentCriteria = parseUrlToCriteria(searchParams);
    if (isEqual(currentCriteria, criteria)) return;

    const newParams = criteriaToSearchParams(criteria);
    setSearchParams(newParams, { replace: true });
  }, [criteria]);
};

export const useAdminCharts = (criteria: ChartsCriteria = defaultChartsCriteria) => {
  const result = apiClient.chart.retrieveChartsData.useQuery(['admin-charts-list', criteria], { query: { criteria: JSON.stringify(criteria) } });
  return { ...result, data: result.data?.body };
};
